// src/services/api.ts
import axios from 'axios'
import { useAuthStore, getTempAccessToken, getTempRefreshToken } from '../store/authStore'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

export const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Función helper para obtener tokens de forma segura desde localStorage
const getTokensFromStorage = (): { accessToken: string | null; refreshToken: string | null } => {
  try {
    const stored = localStorage.getItem('biodacar-auth')
    if (stored) {
      const parsed = JSON.parse(stored)
      return {
        accessToken: parsed.state?.accessToken || null,
        refreshToken: parsed.state?.refreshToken || null
      }
    }
  } catch (e) {
    console.error('Error leyendo tokens de localStorage:', e)
  }
  return { accessToken: null, refreshToken: null }
}

// Función helper para obtener el access token de forma segura
const getAccessToken = (): string | null => {
  // 1. PRIMERO: Token temporal en memoria (más rápido, siempre actualizado)
  const tempToken = getTempAccessToken()
  if (tempToken) {
    return tempToken
  }
  
  // 2. SEGUNDO: Zustand state
  const storeToken = useAuthStore.getState().accessToken
  if (storeToken) {
    return storeToken
  }
  
  // 3. TERCERO: localStorage directo
  const { accessToken } = getTokensFromStorage()
  return accessToken
}

// Función helper para obtener el refresh token de forma segura
const getRefreshToken = (): string | null => {
  // 1. PRIMERO: Token temporal en memoria
  const tempToken = getTempRefreshToken()
  if (tempToken) {
    return tempToken
  }
  
  // 2. SEGUNDO: Zustand state
  const storeToken = useAuthStore.getState().refreshToken
  if (storeToken) {
    return storeToken
  }
  
  // 3. TERCERO: localStorage directo
  const { refreshToken } = getTokensFromStorage()
  return refreshToken
}

// Request interceptor - Agregar token a headers
api.interceptors.request.use((config) => {
  const accessToken = getAccessToken()
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

// Response interceptor - Renovar token expirado
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Si el token expiró (401) y no es ya un retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = getRefreshToken()
        const { setAccessToken, logout } = useAuthStore.getState()
        
        if (!refreshToken) {
          logout()
          window.location.href = '/login'
          return Promise.reject(error)
        }

        const { data } = await axios.post(
          `${API_BASE_URL}/api/auth/refresh`,
          { refreshToken }
        )

        setAccessToken(data.accessToken)
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`

        // Reintentar request original
        return api(originalRequest)
      } catch (refreshError) {
        useAuthStore.getState().logout()
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

export default api
